import { type FC } from "react";
import { Card, CardContent, Stack, Box, CircularProgress } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import qs from "qs";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import { ErrorMessage } from "@/shared/components";
import { FilterProvider } from "@/partner/shared/components/Filter";
import { PlatformPagesList } from "./PlatformPagesList";
import { PlatformPagesFilter } from "./PlatformPagesFilter";
import { CreatePageDialog } from "./CreatePageDialog";
import { EditPageDialog } from "./EditPageDialog";

export const PlatformPages: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const { tab, id: pageId } = qs.parse(searchParams.toString(), {
    ignoreQueryPrefix: true,
  }) as { tab?: string; id?: string };

  const { data: platformData, loading: platformLoading } =
    useCurrentPlatformQuery();
  const currentPlatform = platformData?.currentPlatform;

  const handleEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const handleCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const handleCloseCreateDialog = () => {
    searchParams.delete("tab");
    setSearchParams(searchParams);
  };

  const handleCloseEditDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  if (platformLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentPlatform) {
    return (
      <Card>
        <CardContent>
          <ErrorMessage error={new Error(t("platform.errors.noPlatform"))} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <FilterProvider>
        <Stack spacing={1}>
          <PlatformPagesFilter onCreatePage={handleCreate} />
          <PlatformPagesList onEditPage={handleEdit} />
        </Stack>
      </FilterProvider>

      <CreatePageDialog
        open={tab === "create"}
        onClose={handleCloseCreateDialog}
        onCompleted={handleCloseCreateDialog}
      />

      <EditPageDialog
        open={tab === "edit"}
        pageId={pageId ?? null}
        onClose={handleCloseEditDialog}
        onCompleted={handleCloseEditDialog}
      />
    </>
  );
};
